import type { QueryClient } from '@tanstack/react-query';
import posthog from 'posthog-js';
import type { BankAccountResponse, CreditProduct, MeResponse, MeResponseSE } from '@/api';
import { isMeResponseSE } from '@/api/me/utils';
import type { GetUKDirectDebitResponse } from '@/api/UK/directDebit/types';
import type { BankAccountStatusResponse } from '@/api/UK/me/types';
import type { GetUserIdProtectResponse } from '@/api/user/types';
import type { Country } from '@/constants/config';
import { getConfig } from '@/constants/config';
import { getIdentifiedPostHogDistinctId } from '@/integrations/posthog/utils';
import { ukBankAccountQueryOptions } from '@/pages/BankAccount/UK/queries/ukBankAccountQuery';
import { ukBankAccountStatusQueryOptions } from '@/pages/BankAccount/UK/queries/ukBankAccountStatusQuery';
import { bankAccountQueryOptions } from '@/queries/bankAccountQuery';
import { directDebitQueryOptions } from '@/queries/directDebitQuery';
import { meQueryOptions } from '@/queries/meQuery/meQuery';
import { overdueInfoQueryOptions } from '@/queries/overdueInfoQuery/overdueInfoQuery';
import { selectIsOverdue } from '@/queries/overdueInfoQuery/overdueInfoQuerySelectors';
import { userIdProtectQueryOptions } from '@/queries/userIdProtectQuery';
import { userPaymentProtectionInsuranceQueryOptions } from '@/queries/userPaymentProtectionInsuranceQuery';
import type { OverdueInfoResponse } from '@/types/credit';
import { parseApiError, reportException } from '@/utils/error';
import { assertUnreachable } from '@/utils/typescript';

type AddonStatusProperty = 'active' | 'canceled' | 'inactive' | 'terminated';

type SharedPersonProperties = {
  email_status: 'verified' | 'not_verified' | 'invalid';
  mobile_status: 'verified' | 'not_verified';
  credit_limit: number | null;
  credit_utilization_pct: number | null;
};

type OverduePersonProperties = {
  overdue_status: 'none' | 'standard' | 'overdue_campaign' | 'reset_payback_plan';
  overdue_installment_count: number;
};

type SEPersonProperties = SharedPersonProperties &
  OverduePersonProperties & {
    has_credit: boolean;
    has_debt_consolidation: boolean;
    loan_variant: 'none' | 'loan' | 'loan_id_protect' | 'loan_partner';
    loan_partner_package: 'none' | 'base' | 'plus' | 'premium';
    id_protect_status: AddonStatusProperty;
    id_protect_activation: 'activated' | 'activation_needed' | 'none';
    ppi_status: AddonStatusProperty;
    bank_account_status: 'missing' | 'mandate_not_set' | 'mandate_in_progress' | 'mandate_set';
    payment_provider: 'trustly' | 'zimpler';
  };

type UKPersonProperties = SharedPersonProperties &
  OverduePersonProperties & {
    nominated_account_status: 'incomplete' | 'manual_approval' | 'mandate_missing' | 'ready';
    direct_debit_collection_status: 'none' | 'pending' | 'retrying';
    repayment_day: number | null;
  };

type FIPersonProperties = SharedPersonProperties;

type PersonPropertiesByCountry = {
  SE: SEPersonProperties;
  UK: UKPersonProperties;
  FI: FIPersonProperties;
};

type PersonPropertySource<P> = (queryClient: QueryClient) => Promise<Partial<P>>;

type PersonPropertyValue = string | number | boolean | null;

type SELoanProduct = NonNullable<MeResponseSE['activatedProducts']['loan']>;

const emailStatusProperties = {
  VERIFIED: 'verified',
  NOT_VERIFIED: 'not_verified',
  INVALID: 'invalid',
} satisfies Record<MeResponse['emailStatus'], SharedPersonProperties['email_status']>;

const mobileStatusProperties = {
  VERIFIED: 'verified',
  NOT_VERIFIED: 'not_verified',
} satisfies Record<MeResponse['mobileStatus'], SharedPersonProperties['mobile_status']>;

const resolveCreditProperties = (
  credit: CreditProduct | undefined,
): Pick<SharedPersonProperties, 'credit_limit' | 'credit_utilization_pct'> => {
  if (!credit) {
    return { credit_limit: null, credit_utilization_pct: null };
  }

  if (credit.creditLimit <= 0) {
    return { credit_limit: credit.creditLimit, credit_utilization_pct: null };
  }

  return {
    credit_limit: credit.creditLimit,
    credit_utilization_pct: Math.round((credit.amountUsed / credit.creditLimit) * 100),
  };
};

const resolveSharedProperties = (me: MeResponse): SharedPersonProperties => ({
  email_status: emailStatusProperties[me.emailStatus],
  mobile_status: mobileStatusProperties[me.mobileStatus],
  ...resolveCreditProperties(me.activatedProducts.credit),
});

const readSharedMeProperties: PersonPropertySource<SharedPersonProperties> = async queryClient => {
  const me = await queryClient.query({ ...meQueryOptions(), staleTime: 'static' });

  return resolveSharedProperties(me);
};

const overdueDiscountStatusProperties = {
  NO_DISCOUNT: 'standard',
  OVERDUE_CAMPAIGN: 'overdue_campaign',
  RESET_PAYBACK_PLAN: 'reset_payback_plan',
} satisfies Record<
  OverdueInfoResponse['discountCategory'],
  Exclude<OverduePersonProperties['overdue_status'], 'none'>
>;

const resolveOverdueStatusProperty = (
  overdueInfo: OverdueInfoResponse,
): OverduePersonProperties['overdue_status'] => {
  if (!selectIsOverdue(overdueInfo)) {
    return 'none';
  }

  return overdueDiscountStatusProperties[overdueInfo.discountCategory];
};

const readOverdueProperties: PersonPropertySource<OverduePersonProperties> = async queryClient => {
  const overdueInfo = await queryClient.query({ ...overdueInfoQueryOptions(), staleTime: 'static' });

  return {
    overdue_status: resolveOverdueStatusProperty(overdueInfo),
    overdue_installment_count: overdueInfo.numberOfOverdues,
  };
};

const loanVariantProperties = {
  LOAN: 'loan',
  LOAN_ID_PROTECT: 'loan_id_protect',
  LOAN_PARTNER: 'loan_partner',
} satisfies Record<SELoanProduct['productVariant'], Exclude<SEPersonProperties['loan_variant'], 'none'>>;

const resolveLoanVariantProperty = (
  loan: MeResponseSE['activatedProducts']['loan'],
): SEPersonProperties['loan_variant'] => {
  if (!loan) {
    return 'none';
  }

  return loanVariantProperties[loan.productVariant];
};

const loanPartnerPackageProperties = {
  BASE: 'base',
  PLUS: 'plus',
  PREMIUM: 'premium',
} satisfies Record<
  Extract<SELoanProduct, { productVariant: 'LOAN_PARTNER' }>['productName'],
  Exclude<SEPersonProperties['loan_partner_package'], 'none'>
>;

const resolveLoanPartnerPackageProperty = (
  loan: MeResponseSE['activatedProducts']['loan'],
): SEPersonProperties['loan_partner_package'] => {
  if (loan?.productVariant !== 'LOAN_PARTNER') {
    return 'none';
  }

  return loanPartnerPackageProperties[loan.productName];
};

const paymentProviderProperties = {
  Trustly: 'trustly',
  Zimpler: 'zimpler',
} satisfies Record<MeResponseSE['paymentProvider'], SEPersonProperties['payment_provider']>;

const readSEMeProperties: PersonPropertySource<SEPersonProperties> = async queryClient => {
  const me = await queryClient.query({ ...meQueryOptions(), staleTime: 'static' });

  if (!isMeResponseSE(me)) {
    return resolveSharedProperties(me);
  }

  return {
    ...resolveSharedProperties(me),
    has_credit: me.activatedProducts.credit !== undefined,
    has_debt_consolidation: me.activatedProducts.debtConsolidation !== undefined,
    loan_variant: resolveLoanVariantProperty(me.activatedProducts.loan),
    loan_partner_package: resolveLoanPartnerPackageProperty(me.activatedProducts.loan),
    payment_provider: paymentProviderProperties[me.paymentProvider],
  };
};

const addonStatusProperties = {
  ACTIVE: 'active',
  CANCELED: 'canceled',
  INACTIVE: 'inactive',
  TERMINATED: 'terminated',
} satisfies Record<GetUserIdProtectResponse['status'], AddonStatusProperty>;

const resolveIdProtectActivationProperty = (
  idProtect: GetUserIdProtectResponse,
): SEPersonProperties['id_protect_activation'] => {
  switch (idProtect.status) {
    case 'ACTIVE':
    case 'CANCELED':
      return idProtect.isThirdPartyActive ? 'activated' : 'activation_needed';
    case 'INACTIVE':
    case 'TERMINATED':
      return 'none';
    default:
      return assertUnreachable(idProtect);
  }
};

const readIdProtectProperties: PersonPropertySource<SEPersonProperties> = async queryClient => {
  const idProtect = await queryClient.query({ ...userIdProtectQueryOptions(), staleTime: 'static' });

  return {
    id_protect_status: addonStatusProperties[idProtect.status],
    id_protect_activation: resolveIdProtectActivationProperty(idProtect),
  };
};

const readPaymentProtectionInsuranceProperties: PersonPropertySource<
  SEPersonProperties
> = async queryClient => {
  const paymentProtectionInsurance = await queryClient.query({
    ...userPaymentProtectionInsuranceQueryOptions(),
    staleTime: 'static',
  });

  return { ppi_status: addonStatusProperties[paymentProtectionInsurance.status] };
};

const bankAccountMandateStatusProperties = {
  NOT_SET: 'mandate_not_set',
  IN_PROGRESS: 'mandate_in_progress',
  SET: 'mandate_set',
} satisfies Record<
  BankAccountResponse['directDebitMandateStatus'],
  Exclude<SEPersonProperties['bank_account_status'], 'missing'>
>;

const resolveBankAccountStatusProperty = (
  bankAccount: BankAccountResponse,
): SEPersonProperties['bank_account_status'] => {
  if (!bankAccount.accountNumber) {
    return 'missing';
  }

  return bankAccountMandateStatusProperties[bankAccount.directDebitMandateStatus];
};

const readSEBankAccountProperties: PersonPropertySource<SEPersonProperties> = async queryClient => {
  const bankAccount = await queryClient.query({ ...bankAccountQueryOptions(), staleTime: 'static' });

  return { bank_account_status: resolveBankAccountStatusProperty(bankAccount) };
};

const nominatedAccountStatusProperties = {
  incomplete: 'incomplete',
  manualApproval: 'manual_approval',
} satisfies Record<
  Exclude<BankAccountStatusResponse['accountStatus'], 'complete'>,
  Exclude<UKPersonProperties['nominated_account_status'], 'ready' | 'mandate_missing'>
>;

const resolveNominatedAccountStatusProperty = (
  bankAccountStatus: BankAccountStatusResponse,
): UKPersonProperties['nominated_account_status'] => {
  if (bankAccountStatus.accountStatus === 'complete') {
    return bankAccountStatus.isDirectDebitMandateSet ? 'ready' : 'mandate_missing';
  }

  return nominatedAccountStatusProperties[bankAccountStatus.accountStatus];
};

const readUKBankAccountStatusProperties: PersonPropertySource<UKPersonProperties> = async queryClient => {
  const bankAccountStatus = await queryClient.query({
    ...ukBankAccountStatusQueryOptions(),
    staleTime: 'static',
  });

  return { nominated_account_status: resolveNominatedAccountStatusProperty(bankAccountStatus) };
};

const directDebitCollectionStatusProperties = {
  PENDING: 'pending',
  RETRYING: 'retrying',
} satisfies Record<
  GetUKDirectDebitResponse['status'],
  Exclude<UKPersonProperties['direct_debit_collection_status'], 'none'>
>;

const readUKDirectDebitProperties: PersonPropertySource<UKPersonProperties> = async queryClient => {
  try {
    const directDebit = await queryClient.query({ ...directDebitQueryOptions(), staleTime: 'static' });

    return {
      direct_debit_collection_status: directDebitCollectionStatusProperties[directDebit.status],
    };
  } catch (error) {
    // A 404 means there is no collection in flight.
    if (parseApiError(error).status === 404) {
      return { direct_debit_collection_status: 'none' };
    }

    throw error;
  }
};

const readUKRepaymentDayProperties: PersonPropertySource<UKPersonProperties> = async queryClient => {
  try {
    const bankAccount = await queryClient.query({ ...ukBankAccountQueryOptions(), staleTime: 'static' });

    return { repayment_day: bankAccount.repaymentDay };
  } catch (error) {
    // A 404 means the user has no payment-provider account yet, so no repayment day is chosen.
    if (parseApiError(error).status === 404) {
      return { repayment_day: null };
    }

    throw error;
  }
};

const personPropertySources: {
  readonly [C in Country]: readonly PersonPropertySource<PersonPropertiesByCountry[C]>[];
} = {
  SE: [
    readSEMeProperties,
    readOverdueProperties,
    readIdProtectProperties,
    readPaymentProtectionInsuranceProperties,
    readSEBankAccountProperties,
  ],
  UK: [
    readSharedMeProperties,
    readOverdueProperties,
    readUKBankAccountStatusProperties,
    readUKDirectDebitProperties,
    readUKRepaymentDayProperties,
  ],
  FI: [readSharedMeProperties],
};

export const syncPostHogPersonProperties = async (
  queryClient: QueryClient,
  userId: string,
): Promise<void> => {
  const results = await Promise.allSettled(
    personPropertySources[getConfig().country].map(source => source(queryClient)),
  );

  const properties: Record<string, PersonPropertyValue> = {};

  for (const result of results) {
    if (result.status === 'rejected') {
      reportException(result.reason, { level: 'warning' });
      continue;
    }

    Object.assign(properties, result.value);
  }

  if (Object.keys(properties).length === 0) {
    return;
  }

  if (getIdentifiedPostHogDistinctId() !== userId) {
    return;
  }

  if (queryClient.getQueryData(meQueryOptions().queryKey)?.id !== userId) {
    return;
  }

  posthog.setPersonProperties(properties);
};
